import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Flex, Typography, Tooltip, Popover, Avatar, message } from "antd";
import dayjs from "dayjs";
import { getCompanySettings, getCompanyProfile } from "../../../services/organizationService";
import { getEmployees, getEmployeeCallStats } from "../../../services/employeeService";
import { getTodayBreaks, getEmployeeTodayBreak } from "../../../services/breakService";
import { getCallSummaryReport, getEmployeeCallReport } from "../../../services/reportService";
import usePresence from "../../../hooks/usePresence";
import useKeyboardShortcuts from "../../../hooks/useKeyboardShortcuts";
import "./bottomNavigation.css";

const { Text } = Typography;

/**
 * Format relative time (e.g. "2m ago", "Just now", "1h ago")
 */
function formatRelativeTime(dateString) {
  if (!dateString) return "Not synced";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Not synced";
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

/**
 * Format seconds to minutes/hours string
 */
function formatSecondsToTime(sec = 0) {
  if (!sec || sec <= 0) return "0m";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const remM = m % 60;
    return `${h}h ${remM}m`;
  }
  return `${m}m`;
}

function NavSeparator() {
  return <span className="bottom-nav-separator">|</span>;
}

/**
 * Employee Bottom Widgets:
 * - Target progress
 * - Break status / timer
 * - EasyCalls sync freshness indicator
 */
function EmployeeWidgets({ companyCode, employeeId, autoBreak }) {
  usePresence({ role: "employee", companyCode, employeeId, autoBreak });

  const [target, setTarget] = useState(50);
  const [connectedCalls, setConnectedCalls] = useState(0);
  const [totalCalls, setTotalCalls] = useState(0);
  const [todayBreakSec, setTodayBreakSec] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const loadEmployeeData = useCallback(async () => {
    if (!companyCode || !employeeId) return;

    try {
      const [settingsRes, statsRes, breakRes, empListRes] = await Promise.all([getCompanySettings(companyCode), getEmployeeCallStats(companyCode, employeeId, "today"), getEmployeeTodayBreak(companyCode, employeeId), getEmployees(companyCode)]);

      if (settingsRes?.success && settingsRes.settings?.dailyConnectedCallTarget) {
        setTarget(Number(settingsRes.settings.dailyConnectedCallTarget) || 50);
      }

      if (statsRes?.success && statsRes.stats) {
        setConnectedCalls(statsRes.stats.connectedCalls || statsRes.stats.connected || 0);
        setTotalCalls(statsRes.stats.totalCalls || statsRes.stats.total || 0);
      }

      if (breakRes?.success) {
        setTodayBreakSec(breakRes.totalSeconds || 0);
      }

      if (empListRes?.success && Array.isArray(empListRes.employees)) {
        const currentEmp = empListRes.employees.find((e) => String(e._id) === String(employeeId) || String(e.id) === String(employeeId));
        if (currentEmp?.lastSyncTime) {
          setLastSyncTime(currentEmp.lastSyncTime);
        }
      }
    } catch (err) {
      console.warn("Error loading employee bottom nav metrics:", err);
    }
  }, [companyCode, employeeId]);

  useEffect(() => {
    loadEmployeeData();
    const interval = setInterval(loadEmployeeData, 45000);
    return () => clearInterval(interval);
  }, [loadEmployeeData]);

  const targetPct = useMemo(() => {
    if (target <= 0) return 0;
    return Math.min(100, Math.round((connectedCalls / target) * 100));
  }, [connectedCalls, target]);

  const isOnBreak = autoBreak?.isOnBreak;
  const breakDisplay = isOnBreak ? `On Break (${formatSecondsToTime(autoBreak?.currentBreakSeconds || 0)})` : `Break: ${formatSecondsToTime(todayBreakSec)}`;

  const syncFreshness = useMemo(() => {
    if (!lastSyncTime) return { label: "Not synced", dotClass: "bottom-nav-dot" };
    const diffMin = Math.floor((Date.now() - new Date(lastSyncTime).getTime()) / 60000);
    if (diffMin <= 15) return { label: `Synced ${formatRelativeTime(lastSyncTime)}`, dotClass: "bottom-nav-dot" };
    if (diffMin <= 60) return { label: `Synced ${formatRelativeTime(lastSyncTime)}`, dotClass: "bottom-nav-dot warning" };
    return { label: `Synced ${formatRelativeTime(lastSyncTime)}`, dotClass: "bottom-nav-dot" };
  }, [lastSyncTime]);

  return (
    <>
      {/* Target Progress */}
      <Tooltip title={`Daily connected call target: ${connectedCalls}/${target} (${targetPct}%) | Total calls: ${totalCalls}`}>
        <span className="bottom-nav-item">
          Target: {connectedCalls}/{target} connected ({targetPct}%)
        </span>
      </Tooltip>

      <NavSeparator />

      {/* Break Status / Timer */}
      <Tooltip title={isOnBreak ? "Currently on inactivity break" : "Cumulative break duration today"}>
        <span className="bottom-nav-item">
          {isOnBreak && <span className="bottom-nav-dot warning bottom-nav-pulse" />}
          {breakDisplay}
        </span>
      </Tooltip>

      <NavSeparator />

      {/* EasyCalls App Sync Status */}
      <Tooltip title={`EasyCalls App Last Sync: ${lastSyncTime ? new Date(lastSyncTime).toLocaleString() : "Never synced"}`}>
        <span className="bottom-nav-item">
          <span className={syncFreshness.dotClass} />
          {syncFreshness.label}
        </span>
      </Tooltip>
    </>
  );
}

/**
 * Popover content displaying active/on-break/offline sales reps with profile photos
 */
function ActiveRepsPopoverContent({ employees, activeIdsSet, onBreakIdsSet, onNavigateTab }) {
  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const aId = String(a._id || a.id);
      const bId = String(b._id || b.id);
      const aActive = activeIdsSet.has(aId);
      const bActive = activeIdsSet.has(bId);
      const aBreak = onBreakIdsSet.has(aId);
      const bBreak = onBreakIdsSet.has(bId);

      const aScore = aActive ? 2 : aBreak ? 1 : 0;
      const bScore = bActive ? 2 : bBreak ? 1 : 0;
      if (bScore !== aScore) return bScore - aScore;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [employees, activeIdsSet, onBreakIdsSet]);

  return (
    <div style={{ width: 270, maxHeight: 320, overflowY: "auto", padding: "2px 0" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #f1f5f9" }}>
        <Text strong style={{ fontSize: "0.78rem", color: "#0f172a" }}>
          Live Reps Presence ({employees.length})
        </Text>
        <span style={{ fontSize: "0.7rem", color: "#2563eb", cursor: "pointer", fontWeight: 500 }} onClick={() => onNavigateTab && onNavigateTab("employees")}>
          Manage
        </span>
      </Flex>

      {employees.length === 0 ? (
        <Text type="secondary" style={{ fontSize: "0.75rem" }}>
          No registered sales reps found.
        </Text>
      ) : (
        <Flex vertical gap={6}>
          {sortedEmployees.map((emp) => {
            const empId = String(emp._id || emp.id);
            const isActive = activeIdsSet.has(empId);
            const isOnBreak = onBreakIdsSet.has(empId);

            const statusColor = isActive ? "#10b981" : isOnBreak ? "#f59e0b" : "#94a3b8";
            const statusText = isActive ? "Active" : isOnBreak ? "On Break" : "Offline";

            return (
              <Flex key={empId} align="center" justify="space-between" style={{ padding: "3px 0" }}>
                <Flex align="center" gap={8} style={{ overflow: "hidden" }}>
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <Avatar
                      size={26}
                      src={emp.profilePhoto || undefined}
                      style={{
                        background: "#e2e8f0",
                        color: "#475569",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                      }}
                    >
                      {!emp.profilePhoto && (emp.name || "E").charAt(0).toUpperCase()}
                    </Avatar>
                    <span
                      style={{
                        position: "absolute",
                        bottom: -1,
                        right: -1,
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: statusColor,
                        border: "1.5px solid #ffffff",
                      }}
                    />
                  </div>
                  <Flex vertical style={{ overflow: "hidden" }}>
                    <Text
                      ellipsis
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#1e293b",
                        lineHeight: 1.2,
                        maxWidth: 130,
                      }}
                    >
                      {emp.name || "Employee"}
                    </Text>
                    {emp.mobile && (
                      <Text type="secondary" style={{ fontSize: "0.65rem", lineHeight: 1 }}>
                        {emp.mobile}
                      </Text>
                    )}
                  </Flex>
                </Flex>

                <span
                  style={{
                    fontSize: "0.68rem",
                    fontWeight: 500,
                    color: statusColor,
                    flexShrink: 0,
                  }}
                >
                  {statusText}
                </span>
              </Flex>
            );
          })}
        </Flex>
      )}
    </div>
  );
}

/**
 * Exceeded Break List Popover Content
 */
function ExceededBreakPopoverContent({ overLimitList = [], employees = [] }) {
  const empMap = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      const id = String(e._id || e.id || e.employeeId);
      map[id] = e;
    });
    return map;
  }, [employees]);

  return (
    <div style={{ width: 280, padding: "2px 0" }}>
      <Flex align="center" justify="space-between" style={{ marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid #f1f5f9" }}>
        <Text strong style={{ fontSize: "0.78rem", color: "#dc2626" }}>
          Exceeded Break Limit ({overLimitList.length})
        </Text>
      </Flex>

      <Flex vertical gap={6} style={{ maxHeight: 220, overflowY: "auto" }}>
        {overLimitList.map((item, idx) => {
          const empId = String(item.employeeId);
          const emp = empMap[empId] || {};
          const photo = item.profilePhoto || emp.profilePhoto;
          const name = item.employeeName || emp.name || `Employee #${empId.slice(-4)}`;
          const mobile = emp.mobile || "";
          const totalMin = item.totalMinutes ?? Math.round((item.totalSeconds || 0) / 60);
          const limitMin = item.limitMinutes ?? 60;
          const exceededMin = item.exceededMinutes ?? Math.max(0, totalMin - limitMin);

          return (
            <Flex key={empId || idx} align="center" justify="space-between" style={{ padding: "3px 0" }}>
              <Flex align="center" gap={8} style={{ overflow: "hidden" }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <Avatar
                    size={26}
                    src={photo || undefined}
                    style={{
                      background: "#fee2e2",
                      color: "#dc2626",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                    }}
                  >
                    {!photo && name.charAt(0).toUpperCase()}
                  </Avatar>
                  <span
                    style={{
                      position: "absolute",
                      bottom: -1,
                      right: -1,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: "#ef4444",
                      border: "1.5px solid #ffffff",
                    }}
                  />
                </div>
                <Flex vertical style={{ overflow: "hidden" }}>
                  <Text
                    ellipsis
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#1e293b",
                      lineHeight: 1.2,
                      maxWidth: 130,
                    }}
                  >
                    {name}
                  </Text>
                  {mobile && (
                    <Text type="secondary" style={{ fontSize: "0.65rem", lineHeight: 1 }}>
                      {mobile}
                    </Text>
                  )}
                </Flex>
              </Flex>

              <Flex vertical align="flex-end" style={{ flexShrink: 0 }}>
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: "#dc2626",
                    lineHeight: 1.1,
                  }}
                >
                  {totalMin}m
                </span>
                <span
                  style={{
                    fontSize: "0.62rem",
                    color: "#94a3b8",
                    lineHeight: 1,
                  }}
                >
                  +{exceededMin}m over
                </span>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </div>
  );
}

/**
 * Admin Bottom Widgets:
 * - Active Reps presence with profile photo popup
 * - Today's call volume & connect rate
 * - Over-limit break alerts
 * - Subscription info
 * - 1-Click Quick CSV export
 */
function AdminWidgets({ companyCode, onNavigateTab }) {
  const presence = usePresence({ role: "admin", companyCode });

  const [employees, setEmployees] = useState([]);
  const [totalReps, setTotalReps] = useState(0);
  const [onBreakCount, setOnBreakCount] = useState(0);
  const [overLimitCount, setOverLimitCount] = useState(0);
  const [overLimitList, setOverLimitList] = useState([]);
  const [todayCalls, setTodayCalls] = useState(0);
  const [connectedCalls, setConnectedCalls] = useState(0);
  const [planName, setPlanName] = useState("Enterprise");
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [exporting, setExporting] = useState(false);

  const loadAdminMetrics = useCallback(async () => {
    if (!companyCode) return;

    try {
      const [empRes, breakRes, summaryRes, profileRes] = await Promise.all([getEmployees(companyCode), getTodayBreaks(companyCode), getCallSummaryReport(companyCode, "today"), getCompanyProfile(companyCode)]);

      if (empRes?.success && Array.isArray(empRes.employees)) {
        setEmployees(empRes.employees);
        setTotalReps(empRes.employees.length);
      }

      if (breakRes?.success) {
        const logs = Array.isArray(breakRes.logs) ? breakRes.logs : [];
        const activeBreaks = logs.filter((b) => b.status === "in_progress" || (!b.endTime && b.startTime));
        setOnBreakCount(activeBreaks.length);
        const overLimit = Array.isArray(breakRes.overLimit) ? breakRes.overLimit : [];
        setOverLimitCount(overLimit.length);
        setOverLimitList(overLimit);
      }

      if (summaryRes?.success && summaryRes.summary) {
        setTodayCalls(summaryRes.summary.totalCalls || summaryRes.summary.total || 0);
        setConnectedCalls(summaryRes.summary.connectedCalls || summaryRes.summary.connected || 0);
      }

      if (profileRes?.success && profileRes.company) {
        if (profileRes.company.subscriptionPlan) {
          setPlanName(profileRes.company.subscriptionPlan);
        }
        if (profileRes.company.subscriptionValidUntil) {
          const diffDays = Math.ceil((new Date(profileRes.company.subscriptionValidUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          setDaysRemaining(Math.max(0, diffDays));
        }
      }
    } catch (err) {
      console.warn("Error loading admin bottom nav metrics:", err);
    }
  }, [companyCode]);

  useEffect(() => {
    loadAdminMetrics();
    const interval = setInterval(loadAdminMetrics, 45000);
    return () => clearInterval(interval);
  }, [loadAdminMetrics]);

  const activeIdsSet = useMemo(() => new Set(presence?.activeEmployeeIds || []), [presence?.activeEmployeeIds]);
  const onBreakIdsSet = useMemo(() => new Set(presence?.onBreakEmployeeIds || []), [presence?.onBreakEmployeeIds]);

  const effectiveTotalReps = presence?.totalRegistered || totalReps;
  const effectiveActiveReps = presence?.activeOnline !== undefined && presence?.totalRegistered ? presence.activeOnline : Math.max(0, totalReps - onBreakCount);
  const effectiveOnBreakCount = presence?.onBreak !== undefined && presence?.totalRegistered ? presence.onBreak : onBreakCount;

  const connectRate = todayCalls > 0 ? Math.round((connectedCalls / todayCalls) * 100) : 0;

  const handleQuickExport = async () => {
    if (!companyCode) return;
    setExporting(true);
    try {
      const [reportRes, empRes] = await Promise.all([getEmployeeCallReport(companyCode, "today"), getEmployees(companyCode)]);

      const rows = reportRes?.employees || [];
      const empList = empRes?.employees || [];
      const empMap = {};
      empList.forEach((e) => {
        empMap[e._id] = e;
      });

      if (rows.length === 0) {
        message.info("No call activity recorded today to export.");
        return;
      }

      const headers = ["Employee", "Mobile", "Total Calls", "Connected Calls", "Incoming", "Outgoing", "Missed/Rejected", "Last Synced"];
      const csvData = [
        headers.join(","),
        ...rows.map((r) => {
          const emp = empMap[r.employeeId] || {};
          const name = emp.name || `Rep #${String(r.employeeId).slice(-4)}`;
          const mobile = emp.mobile || "";
          const notPicked = (r.missed || 0) + (r.rejected || 0);
          const sync = emp.lastSyncTime ? new Date(emp.lastSyncTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
          return [`"${name}"`, `"${mobile}"`, r.total || 0, r.connected || 0, r.incoming || 0, r.outgoing || 0, notPicked, `"${sync}"`].join(",");
        }),
      ].join("\n");

      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `DealVoice_Today_Summary_${dayjs().format("YYYYMMDD_HHmm")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success("Today's call summary exported successfully!");
    } catch (err) {
      console.error("Quick export error:", err);
      message.error("Failed to export today's summary.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Active Reps Presence Popover with Profile Photos */}
      <Popover content={<ActiveRepsPopoverContent employees={employees} activeIdsSet={activeIdsSet} onBreakIdsSet={onBreakIdsSet} onNavigateTab={onNavigateTab} />} trigger="hover" placement="top">
        <span className="bottom-nav-item clickable" onClick={() => onNavigateTab && onNavigateTab("employees")}>
          <span className="bottom-nav-dot" />
          {effectiveActiveReps}/{effectiveTotalReps} Reps Active {effectiveOnBreakCount > 0 && `(${effectiveOnBreakCount} on break)`}
        </span>
      </Popover>

      <NavSeparator />

      {/* Today's Team Telemetry Snapshot */}
      <Tooltip title={`Today's live team telemetry: ${connectedCalls} answered calls out of ${todayCalls} total`}>
        <span className="bottom-nav-item clickable" onClick={() => onNavigateTab && onNavigateTab("reports")}>
          Today: {todayCalls} calls • {connectRate}% connect
        </span>
      </Tooltip>

      <NavSeparator />

      {/* Subscription Validity */}
      <Tooltip title="Current company plan & remaining subscription period">
        <span className="bottom-nav-item clickable" onClick={() => onNavigateTab && onNavigateTab("company")}>
          {planName} {daysRemaining !== null ? `• ${daysRemaining}d left` : ""}
        </span>
      </Tooltip>

      {/* Break Exceeded Alert with Employee Profile & Name Popover */}
      {overLimitCount > 0 && (
        <>
          <NavSeparator />
          <Popover
            content={<ExceededBreakPopoverContent overLimitList={overLimitList} employees={employees} />}
            trigger="hover"
            placement="top"
          >
            <span className="bottom-nav-item danger clickable" onClick={() => onNavigateTab && onNavigateTab("reports")}>
              <span className="bottom-nav-dot danger bottom-nav-pulse" />
              {overLimitCount} Exceeded Break
            </span>
          </Popover>
        </>
      )}

      <NavSeparator />

      {/* Quick CSV Export */}
      <Tooltip title="1-Click Quick CSV download of today's call logs summary">
        <button type="button" className="bottom-nav-action-btn" disabled={exporting} onClick={handleQuickExport}>
          {exporting ? "Exporting..." : "CSV Export"}
        </button>
      </Tooltip>
    </>
  );
}

/**
 * Super Admin Bottom Widgets:
 * - Platform totals
 * - Master DB & Cache status
 * - Pending trial requests
 */
function SuperAdminWidgets({ companies = [], onNavigateTab }) {
  const totalCompanies = companies.length;
  const activeCompanies = companies.filter((c) => c.status === "active" || c.isVerified).length;
  const pendingApprovals = companies.filter((c) => c.status === "pending" || !c.isVerified).length;

  return (
    <>
      <Tooltip title={`Platform Tenancy: ${activeCompanies} active companies out of ${totalCompanies} registered`}>
        <span className="bottom-nav-item">
          {totalCompanies} Companies ({activeCompanies} Active)
        </span>
      </Tooltip>

      <NavSeparator />

      <span className="bottom-nav-item">
        <span className="bottom-nav-dot" />
        Master DB: Active • Redis: Ready
      </span>

      {pendingApprovals > 0 && (
        <>
          <NavSeparator />
          <Tooltip title={`${pendingApprovals} company registration/trial approvals pending. Click to review.`}>
            <span className="bottom-nav-item danger clickable" onClick={() => onNavigateTab && onNavigateTab("companies")}>
              <span className="bottom-nav-dot warning bottom-nav-pulse" />
              {pendingApprovals} Pending Approvals
            </span>
          </Tooltip>
        </>
      )}
    </>
  );
}

/**
 * Keyboard Shortcuts Content
 */
function KeyboardShortcutsPopover({ role }) {
  const shortcuts = useMemo(() => {
    const list = [{ key: "⌘K / Ctrl+K", desc: "Global search across leads, deals, & clients" }];

    if (role !== "super_admin") {
      list.push({ key: "L", desc: "Jump to Leads workspace" }, { key: "D", desc: "Jump to Converted Deals" }, { key: "P", desc: "Open Sales Pipeline Kanban" }, { key: "A", desc: "Open Activities & Calendar" });
    }

    if (role === "admin") {
      list.push({ key: "R", desc: "Periodic Reports" }, { key: "E", desc: "Employee Management" }, { key: "S", desc: "Organization Settings" });
    }

    list.push({ key: "Esc", desc: "Close modals & clear global search" });
    return list;
  }, [role]);

  const content = (
    <div style={{ width: 280, padding: "2px 0" }}>
      <Text strong style={{ fontSize: "0.78rem", color: "#0f172a", display: "block", marginBottom: 6 }}>
        Keyboard Shortcuts
      </Text>
      <table className="shortcuts-table">
        <tbody>
          {shortcuts.map((s, idx) => (
            <tr key={idx}>
              <td style={{ width: "45%" }}>
                <kbd>{s.key}</kbd>
              </td>
              <td style={{ color: "#64748b" }}>{s.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="topRight">
      <button type="button" className="bottom-nav-action-btn">
        Shortcuts
      </button>
    </Popover>
  );
}

/**
 * BottomNavigation Component
 *
 * Persistent sticky bottom navigation bar with role-specific telemetry,
 * real-time widgets, system health, and quick actions.
 */
function BottomNavigation({ role = null, companyCode = "", employeeId = "", onNavigateTab = null, autoBreak = null, companies = [], extraContent = null, style = {} }) {
  useKeyboardShortcuts({ role, onNavigateTab });

  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  useEffect(() => {
    const checkOpenModal = () => {
      if (typeof document === "undefined") return;
      const modalSelectors = [
        ".ant-modal-root",
        ".ant-modal-wrap:not([style*='display: none'])",
        ".ant-modal-mask:not([style*='display: none'])",
        ".ant-drawer-open",
        ".invoice-modal-overlay",
        ".quotation-modal-overlay",
        ".modal-overlay",
        ".lead-history-modal-overlay",
        "[role='dialog']",
      ];
      const found = modalSelectors.some((selector) => {
        const el = document.querySelector(selector);
        if (!el) return false;
        const computedStyle = window.getComputedStyle ? window.getComputedStyle(el) : null;
        if (computedStyle && (computedStyle.display === "none" || computedStyle.visibility === "hidden")) {
          return false;
        }
        return true;
      });
      setIsAnyModalOpen(found);
    };

    checkOpenModal();

    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(() => {
        checkOpenModal();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "open"],
      });

      return () => observer.disconnect();
    }
  }, []);

  if (isAnyModalOpen) {
    return null;
  }

  return (
    <footer className="bottom-navigation-container" style={style}>
      {/* Left: Branding */}
      <Flex align="center" gap={6} className="bottom-navigation-left">
        <Text strong style={{ color: "#0f172a", fontSize: "0.75rem", lineHeight: 1 }}>
          DealVoice
        </Text>
      </Flex>

      {/* Center: Live Role Widgets */}
      <div className="bottom-navigation-center">
        {role === "employee" && <EmployeeWidgets companyCode={companyCode} employeeId={employeeId} autoBreak={autoBreak} />}

        {role === "admin" && <AdminWidgets companyCode={companyCode} onNavigateTab={onNavigateTab} />}

        {role === "super_admin" && <SuperAdminWidgets companies={companies} onNavigateTab={onNavigateTab} />}
      </div>

      {/* Right: Shortcuts, Extra Content & Copyright */}
      <Flex align="center" gap={6} className="bottom-navigation-right">
        {extraContent}
        {extraContent && <NavSeparator />}
        <KeyboardShortcutsPopover role={role} />
        <NavSeparator />
        <Text type="secondary" style={{ fontSize: "0.7rem", lineHeight: 1 }}>
          &copy; {new Date().getFullYear()} DealVoice
        </Text>
      </Flex>
    </footer>
  );
}

export default BottomNavigation;
